import React, { useContext, ReactElement, useState } from "react";
import { AppContext } from "../store";
import Loading from "../common/Loading";
import { Badge, Button, Card, Col, Row } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import ORDERS_BY_STAKEHOLDER_QUERY from "./queries/ORDERS_BY_STAKEHOLDER_QUERY";
import { useQuery } from "@apollo/client";
import { EventTypes } from "../store/actions";
import JoinCTA from "../common/JoinCTA";
import VolunteerCTA from "../common/VolunteerCTA";
import { useQueryParam } from "../hooks/useQueryParam";
import useIsAuthorized from "../hooks/useIsAuthorized";
import { loginRequest } from "../utils/authConfig";
import Autocomplete from "../common/Autocomplete";
import Order from "../pages/types/Order";

const Orders = () => {
  const { state, dispatch } = useContext(AppContext);
  const { isAuthenticated, login } = useIsAuthorized();
  const [filtered, setFiltered] = useState<Order[]>();
  const navigate = useNavigate();
  let query = useQueryParam();
  const orderNumber = query.get("orderNumber");
  const lookupId =
    state.orgInfo?.emulated_lookup_id || state.orgInfo?.lookup_id;
  const loading = state.orgInfo?.loading;
  const currentOrder = state.eventInfo.currentOrder;
  const isVolunteer = state.orgInfo?.volunteer_access;
  const isEC = state.orgInfo?.event_coordinator;
  const orderVariables: { lookupId?: string; orderNumber?: string } = {};
  if (isEC) {
    orderVariables.lookupId = state.orgInfo?.emulated_lookup_id;
  }
  if (orderNumber) {
    orderVariables.orderNumber = orderNumber;
  }
  const { loading: ordersLoading } = useQuery(ORDERS_BY_STAKEHOLDER_QUERY, {
    variables: orderVariables,
    fetchPolicy: "no-cache",
    skip: !isVolunteer && (!isEC || (isEC && !orderVariables?.lookupId)),
    onCompleted: (data: any) => {
      dispatch({
        type: EventTypes.SetOrders,
        payload: {
          ...state.eventInfo,
          orders: [...data.ordersByStakeholder],
          currentOrder: currentOrder
            ? currentOrder
            : data.ordersByStakeholder[0],
        },
      });
    },
  });
  const loadDetails = (orderId: string) => {
    navigate(`/order/${orderId}`);
  };
  const displayOrder = (order: any): ReactElement => {
    const lastUpdate = new Date(order.updatedAt || "");
    const dateDisplay = lastUpdate.toLocaleDateString();
    const timeDisplay = lastUpdate.toLocaleTimeString("en-US");
    return (
      <Card key={order.id} className="mb-3">
        <Card.Body>
          <Badge pill bg="primary" className="mb-2">
            {order.state}
          </Badge>
          <Card.Title>
            {order.description}
            <Button
              size="sm"
              variant="link"
              className="ms-2"
              onClick={() => loadDetails(order.id)}
            >
              Edit
            </Button>
          </Card.Title>
          <p>
            This order was last updated on {dateDisplay} at {timeDisplay}.{" "}
          </p>
        </Card.Body>
      </Card>
    );
  };
  const displayOrders = (orders: any[]): ReactElement[] => {
    return orders.map((c) => displayOrder(c));
  };
  if (!loading && !isAuthenticated) {
    return (
      <p className="py-5">
        <Button
          variant="outline-primary"
          size="sm"
          onClick={() => login(loginRequest, "loginRedirect")}
        >
          Login or Sign Up
        </Button>
      </p>
    );
  }
  if (loading || ordersLoading) {
    return <Loading />;
  }
  if (lookupId || isEC) {
    if (!isVolunteer && !isEC) {
      return <VolunteerCTA />;
    }
    const handleSelect = (data: any) => {
      if (data && data.length) {
        const selectedEvent = data[0].eventId;
        const filtered = state?.eventInfo?.orders.filter(
          (o: Order) => o.eventId === selectedEvent
        );
        setFiltered(filtered);
      } else {
        console.log("data", data);
        setFiltered(undefined);
      }
    };
    const orderList = filtered || state?.eventInfo?.orders || [];
    const eventIdSet = new Set(orderList.map((o: Order) => o.eventId));
    const eventIds = Array.from(eventIdSet).map((e) => ({ eventId: e }));
    return orderList.length ? (
      <>
        {!orderNumber && (
          <Row>
            <Col xs={12} md={8} lg={6}>
              <Autocomplete
                id="order-list"
                options={eventIds}
                filterKey={"eventId"}
                label="Find an Order"
                placeholder="Filter by event ID"
                onSelect={handleSelect}
                className="mb-5"
              />
            </Col>
          </Row>
        )}
        {displayOrders(orderList)}
        {orderNumber ? <Link to="/orders/">View all your orders</Link> : null}
      </>
    ) : (
      <>
        <p>No orders open for review.</p>
        <p>
          <Link to="/events/">View your events</Link>, or{" "}
          <Link to="/">go to the home page</Link>.
        </p>
      </>
    );
  }

  return <JoinCTA />;
};

export default Orders;
